import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Home, LogOut, LayoutDashboard, User, Menu } from 'lucide-react';

const Navbar = ({ transparent = false }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <header className={`sticky top-0 z-50 backdrop-blur-md border-b ${transparent ? 'bg-white/85 border-slate-200/80' : 'bg-white border-slate-200'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2" data-testid="navbar-logo">
            <div className="w-9 h-9 rounded-md bg-[#0F2C59] flex items-center justify-center text-white font-black text-lg">P</div>
            <span className="font-display font-extrabold text-lg text-[#0F2C59] tracking-tight hidden sm:block">PropertyByKilo</span>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            <Link to="/search?category=residential" className="px-3 py-2 text-sm font-medium text-slate-700 hover:text-[#0F2C59] rounded-md hover:bg-slate-50" data-testid="nav-buy">Buy</Link>
            <Link to="/search?project_status=new_launch" className="px-3 py-2 text-sm font-medium text-slate-700 hover:text-[#0F2C59] rounded-md hover:bg-slate-50" data-testid="nav-projects">Projects</Link>
            <Link to="/search" className="px-3 py-2 text-sm font-medium text-slate-700 hover:text-[#0F2C59] rounded-md hover:bg-slate-50" data-testid="nav-properties">Properties</Link>
            <Link to="/cities" className="px-3 py-2 text-sm font-medium text-slate-700 hover:text-[#0F2C59] rounded-md hover:bg-slate-50" data-testid="nav-cities">Cities</Link>
            <Link to="/become-broker" className="px-3 py-2 text-sm font-medium text-slate-700 hover:text-[#0F2C59] rounded-md hover:bg-slate-50" data-testid="nav-become-broker">Become a Broker</Link>
          </nav>

          <div className="flex items-center gap-2">
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="sm" className="gap-2" data-testid="user-menu-trigger">
                    <div className="w-8 h-8 rounded-full bg-[#0F2C59] text-white flex items-center justify-center text-xs font-bold">
                      {user.name?.charAt(0)?.toUpperCase()}
                    </div>
                    <span className="hidden sm:block text-sm font-medium">{user.name}</span>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-1.5 text-xs text-slate-500">{user.email}</div>
                  <DropdownMenuSeparator />
                  {user.role === 'broker' && (
                    <DropdownMenuItem onClick={() => navigate('/broker')} data-testid="menu-broker-dashboard">
                      <LayoutDashboard size={16} className="mr-2" /> Broker Dashboard
                    </DropdownMenuItem>
                  )}
                  {(user.role === 'admin' || user.role === 'super_admin') && (
                    <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin-dashboard">
                      <LayoutDashboard size={16} className="mr-2" /> Admin Dashboard
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={() => navigate('/')} data-testid="menu-home">
                    <Home size={16} className="mr-2" /> Home
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="menu-logout">
                    <LogOut size={16} className="mr-2" /> Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" size="sm" className="text-sm font-semibold" data-testid="nav-login">Login</Button>
                </Link>
                <Link to="/register">
                  <Button size="sm" className="bg-[#F97316] hover:bg-[#EA580C] text-white font-semibold" data-testid="nav-signup">Sign Up</Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
